import { useState } from 'react'
import { Copy, ArrowRight } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from './Chip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetDuplicateCandidates,
  useMarkApplicationDuplicate,
} from '../../hooks/backend/interviews'
import {
  type DuplicateCandidate,
  statusLabel,
  matchBasisLabel,
  formatDate,
} from '../../utils/interviews'

export function DuplicateDialog({
  applicationId,
  applicantName,
  highlightSurvivorId,
  onChanged,
}: {
  applicationId: string
  applicantName: string
  highlightSurvivorId?: string | null
  onChanged: () => void
}) {
  const { data, loading, error, trigger } = useGetDuplicateCandidates()
  const { trigger: triggerWithdraw } = useMarkApplicationDuplicate()
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<DuplicateCandidate | null>(null)
  const [busy, setBusy] = useState(false)

  const candidates = (data as DuplicateCandidate[] | undefined) ?? []

  const handleOpen = () => {
    setChosen(null)
    setOpen(true)
    trigger({ application_id: applicationId }, { skipCache: true })
  }

  const handleWithdraw = async () => {
    if (!chosen) return
    setBusy(true)
    try {
      await triggerWithdraw({
        dup_application_id: applicationId,
        survivor_application_id: chosen.application_id,
      }).result
      toast.success('Withdrawn as duplicate')
      setOpen(false)
      setChosen(null)
      onChanged()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not withdraw as duplicate', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpen}
        className="h-11 w-full justify-center sm:h-9 sm:w-auto"
      >
        <Copy className="h-4 w-4" />
        Find duplicate
      </Button>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v && !busy) {
            setOpen(false)
            setChosen(null)
          }
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          {chosen ? (
            <>
              <DialogHeader>
                <DialogTitle>Withdraw as duplicate?</DialogTitle>
                <DialogDescription>
                  Withdraw{' '}
                  <span className="font-medium text-foreground">{applicantName}</span> as a
                  duplicate of{' '}
                  <span className="font-medium text-foreground">
                    {chosen.full_name || 'Unnamed applicant'}
                  </span>
                  ? This cannot fire any email and can be reversed by changing the stage back.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setChosen(null)}
                  disabled={busy}
                  className="h-11 w-full sm:h-9 sm:w-auto"
                >
                  Back
                </Button>
                <Button
                  onClick={handleWithdraw}
                  disabled={busy}
                  className="h-11 w-full sm:h-9 sm:w-auto"
                >
                  {busy && <Spinner className="h-4 w-4" />}
                  Withdraw as duplicate
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Later-stage matches</DialogTitle>
                <DialogDescription>
                  These applications look like the same person and sit further along the
                  pipeline. Select the one to keep and withdraw this card as a duplicate.
                </DialogDescription>
              </DialogHeader>

              {loading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                  <Spinner className="h-4 w-4" /> Searching…
                </div>
              ) : error ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              ) : candidates.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No later-stage matches found.
                </p>
              ) : (
                <div className="space-y-2">
                  {candidates.map((c) => {
                    const isHighlight = highlightSurvivorId === c.application_id
                    return (
                      <div
                        key={c.application_id}
                        className={
                          'flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 ' +
                          (isHighlight
                            ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]'
                            : 'border-border bg-card')
                        }
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-semibold text-foreground">
                              {c.full_name || 'Unnamed applicant'}
                            </span>
                            {isHighlight && <Chip tone="brand">Suggested</Chip>}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span>{statusLabel(c.current_status)}</span>
                            {matchBasisLabel(c.match_basis) && (
                              <span>{matchBasisLabel(c.match_basis)}</span>
                            )}
                            <span>Applied {formatDate(c.submitted_at)}</span>
                          </div>
                        </div>
                        <Button
                          variant={isHighlight ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setChosen(c)}
                          className="h-10 shrink-0 sm:h-8"
                        >
                          Select
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="h-11 w-full sm:h-9 sm:w-auto"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
